import React, { useEffect, useRef, useState } from 'react';
import PropTypes from 'prop-types';
import IntlTelInput from 'react-intl-tel-input';

import 'react-intl-tel-input/dist/main.css';
import { useSelector } from 'react-redux';
import styled from 'styled-components';
import getCountryIso3 from 'country-iso-2-to-3';
import getCountryIso2 from 'country-iso-3-to-2';
import { ParagraphNormal } from '../Paragraph';
import { selectPortalGatewayISOCodesListToIgnoreAreaCode } from '../../store/selectors/settingsSelectors';
import { shouldIgnoreAreaCodeHelper } from '../../util/helpers/phoneNumberHelpers';
import themeColors from '../../assets/styles/themeColors';
import { variables } from '../../assets/styles/variables';

const getBorderColors = (isFocused, hasError) => {
  if (isFocused) {
    return themeColors.colorSvgSemidark;
  }

  if (hasError) {
    return themeColors.colorErrorMessage;
  }

  return themeColors.colorBorderPrimary;
};

const PhoneFiledWrap = styled.div`
  > .intl-tel-input {
    width: 100%;

    .country-list {
      width: 100%;
    }

    input {
      width: 100%;
      padding: 12px;
      border-radius: ${variables.borderRadius.borderRadius};
      color: ${themeColors.colorTextSecondary};
      font-weight: 400;
      font-size: ${({ fontSize }) =>
        fontSize ?? variables.typography.p.small.fontSize};
      text-transform: ${({ hasSuggestion }) =>
        hasSuggestion ? 'capitalize' : 'none'};
      outline: none;
      border: 1px solid
        ${({ isFocused, hasError }) =>
          `${getBorderColors(isFocused, hasError)}}`};
    }

    .selected-flag {
      border-right: 1px solid
        ${({ isFocused, hasError }) =>
          `${getBorderColors(isFocused, hasError)}}`};
    }
  }
`;

const Label = styled(ParagraphNormal)`
  line-height: 2.1;
  font-weight: 600;
`;

export const ErrorMessage = styled(ParagraphNormal)`
  font-size: 0.8rem;
  margin-top: 2px;
  color: ${themeColors.colorErrorMessage};
`;

const PhoneField = ({
  setValue,
  name,
  label,
  errors,
  countryCode,
  defaultValue,
  setCountryIsoCode,
  isIraAccount,
  isPhoneConnectedTo2FA,
}) => {
  const inputRef = useRef(null);
  const iso2 = getCountryIso2(countryCode)?.toLowerCase();
  const [isFocused, setIsFocused] = useState(false);
  const [phoneNumber, setPhoneNumber] = useState('');
  const error = errors[name];
  const selectedISOCodesListToIgnoreAreaCode = useSelector(
    selectPortalGatewayISOCodesListToIgnoreAreaCode,
  );

  useEffect(() => {
    if (inputRef.current && iso2) {
      inputRef.current.setFlag(iso2);
    }
  }, [iso2]);

  const processNumber = (isValid, phone, country) => {
    const shouldIgnoreAreaCode = shouldIgnoreAreaCodeHelper(
      country,
      selectedISOCodesListToIgnoreAreaCode,
    );

    if (shouldIgnoreAreaCode) {
      if (country.iso2 === 'pr') {
        return `+${country.dialCode}${phone}`;
      }

      const phoneNumber = `+${country.dialCode}${phone.substring(3)}`;
      return phoneNumber;
    }

    if (!phone) {
      return '';
    }

    if (phone.includes(`+${country.dialCode}`)) {
      return `${phone}`;
    }

    return `+${country.dialCode}${phone}`;
  };

  const processPhoneNumber = (phone, country) => {
    if (!phone) return '';

    const shouldIgnoreAreaCode = shouldIgnoreAreaCodeHelper(
      country,
      selectedISOCodesListToIgnoreAreaCode,
    );

    // Remove any existing country code to prevent duplication
    const cleanedPhone = phone.replace(/^\+\d+/, '');

    if (shouldIgnoreAreaCode) {
      return country.iso2 === 'pr'
        ? `+${country.dialCode}${cleanedPhone}`
        : `+${country.dialCode}${cleanedPhone.substring(3)}`;
    }
    const iso3 = getCountryIso3(country?.iso2?.toUpperCase());
    setCountryIsoCode(iso3);

    return `+${country.dialCode}${cleanedPhone}`;
  };

  const handlePhoneNumberChange = (isValid, phone, country) => {
    const formattedNumber = processNumber(isValid, phone, country);
    setPhoneNumber(formattedNumber); // Save to state
    setValue(name, formattedNumber, {
      shouldValidate: true,
      shouldDirty: true,
    });
  };

  const handleCountryChange = (_, countryData) => {
    if (phoneNumber) {
      const newNumber = processPhoneNumber(phoneNumber, countryData);
      setPhoneNumber(newNumber);
      setValue(name, newNumber, { shouldValidate: true, shouldDirty: true });
    }
  };
  return (
    <PhoneFiledWrap isFocused={isFocused} hasError={error}>
      {!!label && <Label>{label}</Label>}

      {isPhoneConnectedTo2FA ? (
        <IntlTelInput
          ref={inputRef}
          onPhoneNumberFocus={() => {
            setIsFocused(true);
          }}
          onPhoneNumberBlur={() => {
            setIsFocused(false);
          }}
          onPhoneNumberChange={(isValid, phone, country) => {
            setValue(name, processNumber(isValid, phone, country));
          }}
          fieldId={name}
          fieldName={name}
          defaultValue={defaultValue}
          onlyCountries={isIraAccount ? ['us'] : []}
          preferredCountries={isIraAccount ? [] : ['us']}
          defaultCountry="us"
          autoFocus
          disabled
          autocomplete="tel"
          useMobileFullscreenDropdown={false}
        />
      ) : (
        <IntlTelInput
          ref={inputRef}
          onPhoneNumberFocus={() => {
            setIsFocused(true);
          }}
          onPhoneNumberBlur={() => {
            setIsFocused(false);
          }}
          onSelectFlag={handleCountryChange}
          onPhoneNumberChange={handlePhoneNumberChange}
          fieldId={name}
          fieldName={name}
          defaultValue={defaultValue}
          onlyCountries={isIraAccount ? ['us'] : []}
          preferredCountries={isIraAccount ? [] : ['us']}
          defaultCountry="us"
          autoFocus
          useMobileFullscreenDropdown={false}
        />
      )}

      {!!error && <ErrorMessage>{error.message}</ErrorMessage>}
    </PhoneFiledWrap>
  );
};

PhoneField.propTypes = {
  setValue: PropTypes.func,
  setCountryIsoCode: PropTypes.func,
  name: PropTypes.string,
  countryCode: PropTypes.string,
  label: PropTypes.string,
  errors: PropTypes.shape({}),
  defaultValue: PropTypes.string,
  isIraAccount: PropTypes.bool,
  isPhoneConnectedTo2FA: PropTypes.bool,
};

export default PhoneField;
